import { cn } from '@commenda/utils';
import React from 'react';
import { Button } from '../Button';
import { Checkbox } from '../Checkbox';
import { Icons } from '../Icons';
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger,
} from '../Popover';

export const PickerMenu = ({
  width = 251,
  children,
  className,
}: {
  width?: number;
  children: React.ReactNode;
  className?: string;
}) => (
  <div
    style={{ width }}
    className={cn(
      'flex flex-col overflow-hidden rounded-xl border-1/2 border-border-secondary bg-bg-primary shadow-[0px_2px_8px_0px_rgba(0,0,0,0.04)]',
      className
    )}
  >
    {children}
  </div>
);

export const PickerSearch = ({
  value,
  onChange,
  placeholder = 'Search...',
  label,
  disabled = false,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  label: string;
  disabled?: boolean;
}) => (
  <div className="w-full border-b-1/2 border-border-secondary px-lg">
    <input
      type="text"
      value={value}
      onChange={(event) => onChange(event.target.value)}
      placeholder={placeholder}
      aria-label={label}
      disabled={disabled}
      className="txt-small-regular w-full appearance-none rounded-none border-0 bg-transparent p-0 py-md text-text-primary shadow-none placeholder:text-text-placeholder focus:border-0 focus:shadow-none focus:outline-none focus:ring-0"
    />
  </div>
);

export const PickerList = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <div
    className={cn(
      'flex max-h-72 w-full flex-col overflow-y-auto py-sm',
      className
    )}
  >
    {children}
  </div>
);

export const PickerHeading = ({ children }: { children: React.ReactNode }) => (
  <span className="txt-extra-small-medium w-full px-lg py-md text-text-secondary">
    {children}
  </span>
);

export const PickerEmpty = ({ children }: { children: React.ReactNode }) => (
  <span className="txt-small-regular px-lg py-md text-text-tertiary">
    {children}
  </span>
);

const OPTION_BASE =
  'flex w-full items-center gap-md rounded-sm py-md pl-md pr-lg text-left';

export interface PickerOptionProps {
  label: string;
  leading?: React.ReactNode;
  disabled?: boolean;
  /** Pass to make the row a checkbox row; omit to make it an action row. Multi-select. */
  checked?: boolean | 'indeterminate';
  /** Single-select: a trailing check on the chosen row, with no control on the others. */
  isSelected?: boolean;
  labelAdornment?: React.ReactNode;
  trailing?: React.ReactNode;
  onSelect?: () => void;
  labelClassName?: string;
}

export const PickerOption = ({
  label,
  leading,
  disabled = false,
  checked,
  isSelected = false,
  labelAdornment,
  trailing,
  onSelect,
  labelClassName,
}: PickerOptionProps) => {
  const body = (
    <div className="flex min-w-0 flex-1 items-center gap-sm">
      {leading ? (
        <span className="flex size-6 shrink-0 items-center justify-center">
          {leading}
        </span>
      ) : null}
      <span
        className={cn(
          'txt-small-medium min-w-0 truncate',
          labelAdornment ? '' : 'flex-1',
          disabled ? 'text-text-disabled' : 'text-text-primary',
          labelClassName
        )}
      >
        {label}
      </span>
      {labelAdornment ? (
        <span className="shrink-0">{labelAdornment}</span>
      ) : null}
      {isSelected ? (
        <Icons.Check
          className="size-4 shrink-0 text-fg-brand"
          aria-hidden="true"
        />
      ) : null}
    </div>
  );

  if (checked !== undefined) {
    return (
      <div className="w-full px-sm">
        <label
          className={cn(
            OPTION_BASE,
            disabled ? 'cursor-default' : 'cursor-pointer hover:bg-bg-secondary'
          )}
        >
          <Checkbox
            checked={checked}
            onCheckedChange={onSelect}
            disabled={disabled}
            aria-label={label}
          />
          {body}
        </label>
      </div>
    );
  }

  const renderSelectButton = (className: string) => (
    <Button
      variant="tertiary"
      size="sm"
      fullWidth
      noHover
      onClick={onSelect}
      disabled={disabled}
      aria-label={label}
      className={className}
    >
      {body}
    </Button>
  );

  if (trailing) {
    return (
      <div className="w-full px-sm">
        <div
          className={cn(
            'flex w-full items-center gap-md rounded-sm pr-lg',
            disabled ? '' : 'hover:bg-bg-secondary'
          )}
        >
          <div className="min-w-0 flex-1">
            {renderSelectButton(
              cn(
                'h-auto justify-start gap-md py-md pl-md pr-none text-left',
                disabled && 'cursor-default'
              )
            )}
          </div>
          <span className="shrink-0">{trailing}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full px-sm">
      {renderSelectButton(
        cn(
          'h-auto justify-start gap-md py-md pl-md pr-lg text-left',
          disabled ? 'cursor-default' : 'hover:bg-bg-secondary'
        )
      )}
    </div>
  );
};

export const PickerFooterAction = ({
  label,
  icon,
  onClick,
  disabled = false,
}: {
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
}) => (
  <div className="flex w-full items-center px-sm pb-sm">
    <div className="flex w-full items-center py-xs pl-md pr-lg">
      <Button
        variant="brand-link"
        size="sm"
        onClick={onClick}
        disabled={disabled}
        className="gap-sm"
      >
        {icon}
        <span>{label}</span>
      </Button>
    </div>
  </div>
);

export interface PickerPopoverProps {
  trigger: React.ReactNode;
  align?: 'start' | 'center' | 'end';
  side?: 'top' | 'right' | 'bottom' | 'left';
  disabled?: boolean;
  anchorToRowEnd?: boolean;
  anchorClassName?: string;
  contentClassName?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: React.ReactNode;
}

export const PickerPopover = ({
  trigger,
  align = 'end',
  side,
  disabled = false,
  anchorToRowEnd = false,
  anchorClassName,
  contentClassName,
  open,
  onOpenChange,
  children,
}: PickerPopoverProps) => {
  const popoverTrigger = (
    <PopoverTrigger asChild disabled={disabled}>
      {trigger}
    </PopoverTrigger>
  );

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      {anchorToRowEnd ? (
        <span
          className={cn(
            'relative flex h-8 items-center justify-end',
            anchorClassName
          )}
        >
          <PopoverAnchor asChild>
            <span className="pointer-events-none absolute bottom-0 right-0 size-px" />
          </PopoverAnchor>
          {popoverTrigger}
        </span>
      ) : (
        popoverTrigger
      )}
      <PopoverContent
        align={align}
        side={side}
        className={cn(
          'w-auto border-none bg-transparent p-none shadow-none data-[state=closed]:animate-none data-[state=open]:animate-none',
          contentClassName
        )}
      >
        {children}
      </PopoverContent>
    </Popover>
  );
};
